import React, {useMemo} from "react";
import PropTypes from "prop-types";
import Container from "../layout/Container";
import {useTheme} from "../../theme/ThemeProvider";
import {buildStyles} from "../buildStyles";
import Skeleton from "./Skeleton";
import Text from "./Text";
import FlexContainer from "../layout/FlexContainer";
import Tooltip from "./Tooltip";
import SvgIcon from "./SvgIcon";

const useStyles = (theme) =>
  buildStyles({
    profilePicture: {
      position: "relative",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "40px",
      height: "40px",
      borderRadius: "50%",
      backgroundColor: theme.colors.primary100,
      color: theme.colors.primary700,
      fontWeight: theme.typography.fontWeightBold,
      textTransform: "uppercase",
    },
    profileImage: {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      borderRadius: "50%",
    },
    badgeContainer: {
      position: "absolute",
      width: "22px",
      height: "22px",
      borderRadius: "50%",
    },
  });

const getThemeColors = (theme) => {
  const themeColors = Object.entries(theme.colors).reduce(
    (acc, [key, value]) => {
      if (key.endsWith("100") && !key.startsWith("gray")) {
        const colorBase = key.replace("100", "");
        const color = `${colorBase}600`;
        if (theme.colors[color]) {
          acc.push({
            bg: value,
            text: theme.colors[color],
          });
        }
      }
      return acc;
    },
    [],
  );

  return themeColors;
};

const getHashCode = (str) => {
  return str.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
};

const getColorForName = (name, colors) => {
  const index = getHashCode(name) % colors.length;
  return colors[index];
};

const getInitials = (name) => {
  if (!name) return "";
  const words = name.split(" ");
  return words
    .map((word) => word[0])
    .slice(0, 2)
    .join("");
};

const ProfilePicture = ({
  name = "",
  image,
  svgIcon,
  badges = [],
  skeleton = {enabled: false},
  sx = {},
  textProps = {},
}) => {
  const {theme} = useTheme();
  const classes = useStyles(theme);
  const COLORS = useMemo(() => getThemeColors(theme), []);
  const initials = getInitials(name);
  const {bg, text} = getColorForName(name, COLORS);
  const firstBadge = badges[0];
  const otherBadges = badges.slice(1);

  return skeleton.enabled ? (
    <Skeleton
      width={sx.width ?? "40px"}
      height={sx.height ?? "40px"}
      style={{borderRadius: "50%"}}
    />
  ) : (
    <Container
      as="div"
      className={classes.profilePicture}
      sx={{backgroundColor: bg, color: text, ...sx}}
    >
      {image ? (
        <img src={image} alt={name} className={classes.profileImage} />
      ) : svgIcon ? (
        <SvgIcon
          component={svgIcon}
          sx={{
            width: "16px",
            height: "16px",
            color: text,
            fill: text,
          }}
        />
      ) : (
        <Text variant="textSM" sx={{color: text}} {...textProps}>
          {initials}
        </Text>
      )}
      {!!badges.length && (
        <FlexContainer
          justifyContent="center"
          alignItems="center"
          className={classes.badgeContainer}
          sx={{bottom: "-7px", right: !!otherBadges.length ? "5px" : "-7px"}}
        >
          {firstBadge}
        </FlexContainer>
      )}
      {!!otherBadges.length && (
        <Tooltip
          sx={{
            tooltip: {transform: "translate(11px, 6px)"},
            trigger: {zIndex: 2},
          }}
          tooltipContent={
            <FlexContainer gap={theme.spacing(1)}>
              {otherBadges.map((b) => b)}
            </FlexContainer>
          }
        >
          <FlexContainer
            justifyContent="center"
            alignItems="center"
            className={classes.badgeContainer}
            sx={{
              backgroundColor: theme.colors.gray900,
              bottom: "-6px",
              right: "-7px",
              width: "19px",
              height: "19px",
              cursor: "pointer",
            }}
          >
            <Text
              variant="textXXS"
              weight="medium"
              color="white"
            >{`+${otherBadges.length}`}</Text>
          </FlexContainer>
        </Tooltip>
      )}
    </Container>
  );
};

ProfilePicture.propTypes = {
  name: PropTypes.string,
  image: PropTypes.string,
  svgIcon: PropTypes.any,
};

export default ProfilePicture;
